import { useEffect, useState, useId } from "react";
import { loadSites, falloLaCargaDeSites, type Site } from "../lib/sites";

/**
 * Selector de residencial, compartido por Usuarios, Solicitudes, Eventos y
 * Mensajes. Existe para que las cuatro secciones filtren igual: cuando cada
 * pantalla se inventaba su propio filtro, "todos" significaba una cosa en Eventos
 * y otra en Usuarios.
 *
 * No se pinta si quien mira solo tiene un residencial (una bibliotecaria): un
 * desplegable de un elemento no es una opción, es ruido. En ese caso lo elige
 * por su cuenta (ver abajo).
 */
export function SiteFilter({
  value,
  onChange,
  label = "Ver el residencial",
  todosLabel = "Todos los residenciales",
  requerido = false,
}: {
  value: string;
  onChange: (code: string) => void;
  label?: string;
  todosLabel?: string;
  /** Sin opción "todos": para formularios donde hay que elegir uno. */
  requerido?: boolean;
}) {
  // Une cada <label> con su campo (WCAG 1.3.1): sin esto el lector anunciaba
  // «cuadro de texto» a secas y un clic en la etiqueta no enfocaba nada.
  const uid = useId();
  const sites = useSites();

  /*
   * Con UN solo residencial el selector no se pinta, pero la elección no puede
   * quedarse vacía: los formularios (alta de usuario, visitas, módulos) exigen
   * una sede, y quien solo tiene una no veía ni el desplegable ni forma de
   * elegirla — el formulario le decía "elige un residencial" sin darle dónde.
   * Si solo hay una opción, esa es la respuesta: se elige sola.
   */
  const unica = sites.length === 1 ? sites[0].code : "";
  useEffect(() => {
    if (unica && !value) onChange(unica);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unica, value]);

  if (sites.length <= 1) return null;

  return (
    <div className="field site-filter">
      <label htmlFor={`${uid}-campo`}>{label}</label>
      <select id={`${uid}-campo`} className="service-select" value={value} onChange={(e) => onChange(e.target.value)}>
        {/* Con `requerido` y sin valor hace falta un placeholder: sin él el navegador
            enseña el primer residencial de la lista mientras el estado sigue vacío, y
            el formulario contesta "elige un residencial" señalando uno ya visible. */}
        {requerido
          ? !value && <option value="">Elige un residencial…</option>
          : <option value="">{todosLabel}</option>}
        {sites.map((s) => (
          <option key={s.code} value={s.code}>
            {s.name} — {s.municipio}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Las sedes del alcance, ya cargadas. Comparte la caché de lib/sites. */
export function useSites(): Site[] {
  return useSitesEstado().sites;
}

/**
 * Igual, pero diciendo además si todavía está pidiéndolas.
 *
 * Hace falta porque `[]` significaba dos cosas distintas: "aún no han llegado" y
 * "llegaron y no hay ninguna". `loadSites()` se traga los errores y devuelve `[]`,
 * así que con la API caída el rótulo del sidebar se quedaba en "Cargando
 * residencial…" para siempre, en todas las pantallas.
 */
export function useSitesEstado(): { sites: Site[]; cargando: boolean; error: boolean } {
  const [estado, setEstado] = useState<{ sites: Site[]; cargando: boolean; error: boolean }>({
    sites: [],
    cargando: true,
    error: false,
  });
  useEffect(() => {
    let alive = true;
    loadSites().then((s) => alive && setEstado({
      sites: s, cargando: false, error: s.length === 0 && (falloLaCargaDeSites?.() ?? false),
    }));
    return () => {
      alive = false;
    };
  }, []);
  return estado;
}

/** Nombre legible de una sede por su código, para las etiquetas de las listas. */
export function nombreSede(sites: Site[], code: string | undefined | null): string {
  if (!code) return "Sin residencial";
  return sites.find((s) => s.code === code)?.name ?? code;
}
